import { Mail, Phone, MapPin, Send, CheckCircle, X } from 'lucide-react';
import { useState, useEffect } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showPopup, setShowPopup] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log('Form submitted:', formData);
    setShowPopup(true);
    setFormData({ name: '', email: '', message: '' });
  };

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-regal-gray" itemScope itemType="https://schema.org/ContactPage">
      {/* Success Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-regal-gray rounded-xl border-2 border-regal-red max-w-md w-full p-6 relative animate-fade-in">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <div className="flex items-start gap-4">
              <div className="bg-regal-red/20 p-2 rounded-full flex-shrink-0">
                <CheckCircle className="w-8 h-8 text-regal-red" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                <p className="text-gray-300 leading-relaxed">
                  Thank you for your message! We'll get back to you soon.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white" itemprop="headline">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-400" itemprop="description">
              Have questions? We're here to help. Reach out and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6" itemScope itemType="https://schema.org/Organization">
              <div className="flex items-start gap-4">
                <div className="bg-regal-red/20 p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-regal-red" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Email</h3>
                  <a href="mailto:info@regal-play.com" className="text-gray-300 hover:text-regal-red transition-colors" itemprop="email">
                    info@regal-play.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-regal-red/20 p-3 rounded-lg">
                  <Phone className="w-6 h-6 text-regal-red" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Phone</h3>
                  <a href="tel:+440544403303" className="text-gray-300 hover:text-regal-red transition-colors" itemprop="telephone">
                    +44-0544-403-303
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-regal-red/20 p-3 rounded-lg">
                  <MapPin className="w-6 h-6 text-regal-red" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Address</h3>
                  <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                    <p className="text-gray-300"><span itemProp="addressLocality">London</span>, <span itemProp="addressCountry">United Kingdom</span></p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-regal-dark p-8 rounded-xl border border-gray-800">
              <div className="mb-6">
                <label htmlFor="name" className="block text-white font-semibold mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                  className="w-full bg-regal-gray border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-regal-red transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-white font-semibold mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  className="w-full bg-regal-gray border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-regal-red transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-white font-semibold mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  rows="5"
                  className="w-full bg-regal-gray border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-regal-red transition-colors resize-none"
                  placeholder="Your message..."
                />
              </div>

              <button
                type="submit"
                className="w-full bg-regal-red hover:bg-red-700 text-white py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
              >
                Send Message
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

